import type { BotsManagementWsEvent } from "features/ws/botsManagementWs";

/**
 * What the major-job panel is showing when an event arrives. Read at that
 * moment, never captured: the panel subscribes once and keeps its refs.
 */
export type PanelWsContext = {
  userId: unknown;
  shownMajorId: number | null;
};

/**
 * An event that names a foreign user or a foreign major job is dropped
 * without a request - the group is a broadcast to every open panel, so most
 * of what arrives during busy hours is other people's runs. A payload with the
 * fields missing (an old bot image publishes bare ids) stays conservative:
 * refetch. New jobs of this major job need no special case - their events
 * carry this major's id and the list refetch finds them.
 */
export function eventConcernsPanel(
  userIdRaw: unknown,
  majorIdRaw: unknown,
  ctx: PanelWsContext,
): boolean {
  const eventUserId = userIdRaw == null ? null : Number(userIdRaw);
  if (eventUserId != null && eventUserId !== Number(ctx.userId)) return false;
  const eventMajorId = majorIdRaw == null ? null : Number(majorIdRaw);
  if (
    eventMajorId != null &&
    ctx.shownMajorId != null &&
    eventMajorId !== ctx.shownMajorId
  ) {
    return false;
  }
  return true;
}

export type PanelWsRoute = {
  /** The SSN tab reloads when its own job or task is named, whoever's they are. */
  ssnJobId: number | null;
  ssnTaskId: number | null;
  /**
   * The job list refetch (include=tasks). It carries every job row's stats -
   * the counters, the progress bar, the roster notice - and the task rows, so a
   * burst of events costs one request per window instead of a fan-out.
   */
  refreshList: boolean;
};

const TASK_EVENTS = ["task.updated", "task2fa.updated", "live_view.updated"];

/**
 * What one event asks of the panel. An event is a refetch trigger, never a
 * patch of the cache: a bot's throttled progress event refetches the list, and
 * the server answers with the counters and the "no record yet" count as they
 * stand now.
 */
export function routePanelWsEvent(
  payload: BotsManagementWsEvent,
  ctx: PanelWsContext,
): PanelWsRoute {
  const jobId = payload.job?.job_id;
  if (payload.event === "job.updated" && jobId != null) {
    return {
      ssnJobId: Number(jobId),
      ssnTaskId: null,
      refreshList: eventConcernsPanel(
        payload.job?.user_id,
        payload.job?.major_job_id,
        ctx,
      ),
    };
  }
  const taskId = payload.task?.task_id;
  const eventName = payload.event ?? "task.updated";
  if (taskId == null || !TASK_EVENTS.includes(eventName)) {
    return { ssnJobId: null, ssnTaskId: null, refreshList: false };
  }
  return {
    ssnJobId: null,
    ssnTaskId: Number(taskId),
    refreshList: eventConcernsPanel(
      payload.task?.user_id,
      payload.task?.major_job_id,
      ctx,
    ),
  };
}

export type Coalescer = {
  schedule: (key: string, action: () => void, delayMs: number) => void;
  cancelAll: () => void;
};

/**
 * Coalesce, never postpone: a batch emits an event per task, and restarting
 * the timer on every one of them (a plain debounce) let a busy job push its
 * refetch back indefinitely - the table then sat frozen until the guard poll.
 * The first event of a window schedules the action; the rest ride it.
 */
export function createCoalescer(): Coalescer {
  const timers: Record<string, ReturnType<typeof setTimeout>> = {};
  return {
    schedule(key, action, delayMs) {
      if (timers[key]) return;
      timers[key] = setTimeout(() => {
        delete timers[key];
        action();
      }, delayMs);
    },
    cancelAll() {
      Object.keys(timers).forEach((key) => {
        clearTimeout(timers[key]);
        delete timers[key];
      });
    },
  };
}
